/**
 * Selectors for the Portfolio Overview page (spec §17), the one report page
 * that reads many projects at once.
 *
 * Same two rules as `intelligence.ts`, which this builds on: a KPI that could
 * not be computed has no row at all, so missing is "unknown" and never 0; and
 * labels come with the values, so nothing here renames a KPI.
 *
 * The one thing worth stating explicitly: **which projects are in here is the
 * backend's decision.** `/intelligence/kpis/portfolio` returns exactly the
 * projects the caller may list, so nothing on this page filters rows by
 * visibility - doing so would be a second, drifting definition of it.
 */
import { PortfolioHistoryDto, PortfolioProjectDto } from '../../api/model';
import {
  HistoryPoint,
  KPI,
  categorical,
  healthKeyFromStatus,
  indexProjectKpis,
  numeric,
} from './intelligence';
import { getProjectHealthStatus, ProjectHealthKey } from './reportData';

/**
 * The KPIs the portfolio page draws, and therefore the only ones it asks the
 * endpoint for. Health plus the four dimension scores of §12, the operative
 * progress, and the 7-day health delta - the columns of the spec's own
 * Dashboard list view (p. 35).
 */
export const PORTFOLIO_KPI_KEYS = [
  KPI.projectHealthScore,
  KPI.projectHealthStatus,
  KPI.readinessScore,
  KPI.executionScore,
  KPI.qualityScore,
  KPI.dependencyScore,
  KPI.projectProgress,
  KPI.projectHealthTrend,
  // the two axes and the bubble size of the Health & Attention Matrix
  KPI.criticalAttentionRate,
  KPI.deliverableCount,
] as const;

/**
 * The heatmap's columns (spec §17, "Portfolio Heatmap nach Dimension").
 *
 * The mockup shows a sixth "Governance" column between Dependencies and
 * Health. It is **dropped for V1** by the customer's decision (Entwicklerfragen
 * 1-3, §3): governance-adjacent aspects - responsibilities, structure, planning
 * data, QA planning - are already inside Project Readiness, and a separate
 * Governance score would double-count them. It can be revisited if Qlario ever
 * models quality gates, approvals or escalations of its own.
 */
export const HEATMAP_COLUMNS = [
  // `tab` is the project page a cell opens - "in das Projekt und möglichst in die betroffene Dimension"
  { key: KPI.readinessScore, labelKey: 'readiness', tab: 'readiness' },
  { key: KPI.executionScore, labelKey: 'execution', tab: 'execution' },
  { key: KPI.qualityScore, labelKey: 'quality', tab: 'quality' },
  { key: KPI.dependencyScore, labelKey: 'dependency', tab: 'dependencies' },
  { key: KPI.projectHealthScore, labelKey: 'health', tab: 'overview' },
] as const;

/** One project as every widget on the page needs it. */
export interface PortfolioRow {
  id: number;
  name: string;
  subtitle?: string;
  method?: string;
  characters: string[];
  /** 0-100, or undefined when the score was not computable. */
  health?: number;
  healthKey?: ProjectHealthKey;
  /** Dimension scores by KPI key, absent where not computable. */
  scores: Map<string, number>;
  progress?: number;
  healthTrend?: number;
  /** Share of HIGH-criticality deliverables needing attention; absent when the project has none. */
  attentionRate?: number;
  /** How many deliverables the project has - the matrix's bubble size, never "project size". */
  deliverableCount?: number;
  openAlerts: number;
  /** When this project's KPIs were last computed; absent if they never were. */
  lastComputedAt?: string;
}

/**
 * Turns the endpoint's rows into view models, dropping any row without an id
 * (which nothing could key, link to, or rank).
 */
export function portfolioRows(projects: PortfolioProjectDto[] | undefined): PortfolioRow[] {
  return (projects ?? [])
    .filter((project): project is PortfolioProjectDto & { projectId: number } =>
      project.projectId != null,
    )
    .map((project) => {
      // the row's KPIs are project-scoped values, which is exactly what this indexes
      const index = indexProjectKpis(project.kpis);
      const health = numeric(index, KPI.projectHealthScore);

      const scores = new Map<string, number>();
      for (const key of PORTFOLIO_KPI_KEYS) {
        const value = numeric(index, key);
        if (typeof value === 'number') scores.set(key, value);
      }

      return {
        id: project.projectId,
        name: project.name || `#${project.projectId}`,
        subtitle: project.description || project.projectType || undefined,
        method: project.method,
        characters: project.projectCharacters ?? [],
        health,
        // Prefer the engine's own status over re-deriving it from the score.
        healthKey:
          healthKeyFromStatus(categorical(index, KPI.projectHealthStatus)) ??
          (health != null ? getProjectHealthStatus(health).key : undefined),
        scores,
        progress: numeric(index, KPI.projectProgress),
        healthTrend: numeric(index, KPI.projectHealthTrend),
        attentionRate: numeric(index, KPI.criticalAttentionRate),
        deliverableCount: numeric(index, KPI.deliverableCount),
        openAlerts: project.openAlertCount ?? 0,
        lastComputedAt: project.lastComputedAt ?? undefined,
      };
    });
}

/**
 * The same rows keyed by project id, for joining onto a project list that was
 * loaded separately (the Dashboard). A project with no row - never recomputed,
 * or not in the portfolio - simply has no entry, which the caller renders as
 * unknown rather than zero.
 */
export function portfolioRowsById(
  projects: PortfolioProjectDto[] | undefined,
): Map<number, PortfolioRow> {
  return new Map(portfolioRows(projects).map((row) => [row.id, row]));
}

/**
 * Whether the engine has actually produced anything for a project.
 *
 * The portfolio endpoint returns a row for **every** visible project, including
 * one the engine has never run for - that row simply carries no values. Callers
 * that would otherwise draw an empty ring and a line of dashes (the Dashboard
 * card) should ask this first: showing nothing says more than showing nothing
 * measured.
 */
export function hasComputedKpis(row: PortfolioRow | undefined): row is PortfolioRow {
  return !!row && (row.scores.size > 0 || row.openAlerts > 0);
}

export interface PortfolioSummary {
  total: number;
  /** Mean health across the projects whose score was computable, or undefined when none was. */
  averageHealth?: number;
  byHealth: Record<ProjectHealthKey, number>;
  openAlerts: number;
}

/**
 * The header tiles. The counts use the engine's four health states rather than
 * the mockup's three - collapsing At Risk into Critical would hide a
 * distinction `ph.project_health_status` actually makes.
 *
 * A project whose health is not computable counts towards `total` but towards
 * no band and towards no average: it is unknown, not zero.
 */
export function portfolioSummary(rows: PortfolioRow[]): PortfolioSummary {
  const byHealth: Record<ProjectHealthKey, number> = {
    onTrack: 0,
    watch: 0,
    atRisk: 0,
    critical: 0,
  };

  let scoreSum = 0;
  let scored = 0;
  let openAlerts = 0;

  for (const row of rows) {
    if (row.healthKey) byHealth[row.healthKey] += 1;
    if (typeof row.health === 'number') {
      scoreSum += row.health;
      scored += 1;
    }
    openAlerts += row.openAlerts;
  }

  return {
    total: rows.length,
    averageHealth: scored > 0 ? scoreSum / scored : undefined,
    byHealth,
    openAlerts,
  };
}

/**
 * The oldest {@code lastComputedAt} across the portfolio - how stale the
 * <em>stalest</em> project on the page is.
 *
 * The oldest rather than the newest on purpose: a portfolio view is only as
 * current as its least-recently-computed project, and quoting the newest would
 * describe one row while implying all of them. Projects that have never been
 * computed carry no timestamp and are left out; they are visibly blank in the
 * rows themselves.
 */
export function oldestComputedAt(rows: PortfolioRow[]): string | undefined {
  let oldest: string | undefined;
  for (const row of rows) {
    if (row.lastComputedAt && (oldest === undefined || row.lastComputedAt < oldest)) {
      oldest = row.lastComputedAt;
    }
  }
  return oldest;
}

/** One bubble of the Health & Attention Matrix. */
/** The four dimension scores, for naming a project's weakest one. */
const DIMENSION_KEYS = [
  { key: KPI.readinessScore, labelKey: 'readiness' },
  { key: KPI.executionScore, labelKey: 'execution' },
  { key: KPI.qualityScore, labelKey: 'quality' },
  { key: KPI.dependencyScore, labelKey: 'dependency' },
] as const;

/**
 * A project's main driver as the portfolio can see it: its lowest dimension
 * score. Undefined when no dimension is computable.
 */
export function weakestDimension(row: PortfolioRow): string | undefined {
  let weakest: { labelKey: string; value: number } | undefined;
  for (const dimension of DIMENSION_KEYS) {
    const value = row.scores.get(dimension.key);
    if (typeof value === 'number' && (!weakest || value < weakest.value)) {
      weakest = { labelKey: dimension.labelKey, value };
    }
  }
  return weakest?.labelKey;
}

/**
 * "Projekte mit Handlungsbedarf" (Detailmasken §8.4) - deliberately not a
 * best/worst ranking. A project is on the list when it is not On Track or has
 * open findings; the lowest health comes first, then the most open findings.
 */
export function needsAttention(rows: PortfolioRow[], limit?: number): PortfolioRow[] {
  const listed = rows
    .filter((row) => (row.healthKey != null && row.healthKey !== 'onTrack') || row.openAlerts > 0)
    .sort(
      (a, b) =>
        (a.health ?? 101) - (b.health ?? 101) || b.openAlerts - a.openAlerts || a.name.localeCompare(b.name),
    );
  return limit != null ? listed.slice(0, limit) : listed;
}

/**
 * The portfolio-wide trend line. Days where no project had a value carry no
 * point, so the line breaks rather than dropping to zero - same rule as the
 * per-project history.
 */
export function portfolioHistoryPoints(series: PortfolioHistoryDto | undefined): HistoryPoint[] {
  return (series?.points ?? [])
    .filter((point) => typeof point.averageValue === 'number' && point.date)
    .map((point) => ({ date: point.date as string, value: point.averageValue as number }));
}
